import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "/app/src/components/generic/components/search";
import { carrierService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Carrier as CarrierType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Renders the carriers table with search, pagination, and sorting features.
 *
 * @returns {JSX.Element} The rendered carriers UI component.
 */
export default function Carriers() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const columnHelper = createColumnHelper<CarrierType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
    ],
    [columnHelper, t],
  );

  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );

  const goToCarrier = useCallback(
    (carrierId: number) => {
      navigate(`/explorer/carriers/${carrierId}`);
    },
    [navigate],
  );

  const {
    data: carriers,
    count: carriersCount,
    isFetching,
  } = usePaginatedData<CarrierType>({
    queryKey: ["carriers"],
    searchString,
    page,
    pageSize,
    sort,
    service: carrierService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: carriersCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:carriers")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={carriers}
        tableColumns={columns}
        length={carriersCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToCarrier}
        emptyText={t("translation:no_carriers")}
      />
    </div>
  );
}
